import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useCanManageBlueprint from "shared/Blueprint/access/useCanManageBlueprint";
import useCanDeleteVersion from "shared/Templates/access/useCanDeleteVersion";
import { BlueprintState } from "types/generated";

import { TemplateActions } from "../TemplatesWorkbench/FeatureGate/types";
import TemplatesFeatureGateCallout from "../TemplatesWorkbench/FeatureGate/Callout";
import BlueprintStateBadge from "../Blueprints/StateBadge";
import { TemplateContext } from "../Template/Context";
import { showCreateVersionDrawer } from "../Template/Versions/CreateVersionDrawer";
import { showDeleteConfirmation } from "../Template/Versions/DeleteConfirmation";
import Deploy from "./Deploy";
import TemplateVersionDeployments from "./Deployments";
import { showVersionDetailsDrawer } from "./DetailsDrawer";
import VersionTemplateBody from "./TemplateBody";
import UpdateDeployment from "./UpdateDeployment";
import useTemplateVersion from "./useTemplateVersion";
import TemplateFeatureGateTooltip from "../TemplatesWorkbench/FeatureGate/Tooltip";

const TemplateVersionPage = () => {
  const navigate = useNavigate();

  const { versionId, templateId } = useParams<{ versionId: string; templateId: string }>();

  const { error, loading, data } = useTemplateVersion(templateId, versionId);

  const handleOpenEditDrawer = () => {
    if (data?.blueprintVersionedGroup?.blueprintVersion) {
      showCreateVersionDrawer({
        version: data?.blueprintVersionedGroup?.blueprintVersion,
        templateId: templateId as string,
      });
    }
  };

  const handleOpenCloneDrawer = () => {
    if (data?.blueprintVersionedGroup?.blueprintVersion) {
      showCreateVersionDrawer({
        version: data?.blueprintVersionedGroup?.blueprintVersion,
        templateId: templateId as string,
        isCloneMode: true,
      });
    }
  };

  const handleOpenDetailsDrawer = () => {
    if (data?.blueprintVersionedGroup?.blueprintVersion) {
      showVersionDetailsDrawer({
        item: data.blueprintVersionedGroup.blueprintVersion,
        ...(canManageBlueprint.result && { onEdit: handleOpenEditDrawer }),
      });
    }
  };

  useBreadcrumbs(
    [
      {
        title: "Templates Workbench",
        link: "/templates-workbench",
      },
      {
        title: data?.blueprintVersionedGroup?.name || "",
        link: `/template/${data?.blueprintVersionedGroup?.id}`,
      },
      {
        title: data?.blueprintVersionedGroup?.blueprintVersion?.version || "",
      },
    ],
    [data?.blueprintVersionedGroup?.blueprintVersion?.version]
  );

  const canManageBlueprintFn = useCanManageBlueprint();
  const canDeleteVersionFn = useCanDeleteVersion();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.blueprintVersionedGroup?.blueprintVersion) {
    return <PageLoading />;
  }

  if (!data?.blueprintVersionedGroup?.blueprintVersion) {
    return <NotFoundPage />;
  }

  const version = data.blueprintVersionedGroup.blueprintVersion;
  const versionSchema = data.templateSchema;
  const isPublished = version.state === BlueprintState.Published;

  const handleDeleteClick = () => {
    showDeleteConfirmation({
      version: `${version.name} - ${version.version}`,
      id: version.id,
    }).then(() => navigate(`/template/${data.blueprintVersionedGroup.id}`));
  };

  const versionUrl = `/template/${data.blueprintVersionedGroup.id}/version/${data.blueprintVersionedGroup.blueprintVersion.id}`;

  const canManageBlueprint = canManageBlueprintFn(version);
  const canDeleteVersion = canDeleteVersionFn(version.space.id, version.deploymentsCount);

  return (
    <TemplateContext.Provider
      value={{
        template: data.blueprintVersionedGroup,
        templateUrl: `/template/${data.blueprintVersionedGroup.id}`,
      }}
    >
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{version.version}</ViewHeaderTitle>
            <BlueprintStateBadge state={version.state} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>

            <DropdownMenuEllipsis tooltip="Version actions" dotsSize="medium">
              {!isPublished && (
                <TemplateFeatureGateTooltip
                  on={(props) => (
                    <DropdownMenuItem {...props} onAction={handleOpenEditDrawer}>
                      Edit metadata
                    </DropdownMenuItem>
                  )}
                  template={data.blueprintVersionedGroup}
                  action={TemplateActions.EditTemplate}
                  tooltipPlacement="left"
                />
              )}

              <TemplateFeatureGateTooltip
                on={(props) => (
                  <DropdownMenuItem {...props} onAction={handleOpenCloneDrawer}>
                    Duplicate
                  </DropdownMenuItem>
                )}
                template={data.blueprintVersionedGroup}
                action={TemplateActions.EditTemplate}
                tooltipPlacement="left"
              />

              {(canDeleteVersion.result || !!canDeleteVersion.message) && (
                <DropdownMenuItem
                  onAction={handleDeleteClick}
                  danger
                  isDisabled={!canDeleteVersion.result}
                  tooltip={canDeleteVersion.message}
                  tooltipPlacement="left"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderNavigation>
          <Tab end to={versionUrl} label="Template body" />
          <Tab to={`${versionUrl}/deployments`} label="Deployments" />
        </ViewHeaderNavigation>
      </ViewHeader>
      <TemplatesFeatureGateCallout />
      <Routes>
        <Route
          index
          element={
            <VersionTemplateBody
              version={version}
              versionSchema={versionSchema}
              template={data.blueprintVersionedGroup}
            />
          }
        />

        <Route
          path="deployments"
          element={
            <TemplateVersionDeployments
              templateId={data.blueprintVersionedGroup.id}
              versionId={version.id}
              template={data.blueprintVersionedGroup}
              versionState={version.state}
            />
          }
        />

        <Route path="deploy" element={<Deploy />} />

        <Route path="deployments/:deploymentId/update" element={<UpdateDeployment />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </TemplateContext.Provider>
  );
};

export default TemplateVersionPage;
